import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LegalPages.css';

const Support: React.FC = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const faqs = [
    {
      question: "How do I add a child to my account?",
      answer: "Go to the Children tab and tap 'Add Child'. Enter their name and date of birth. You can add multiple children to manage all their activities in one place."
    },
    {
      question: "How do I connect club apps like Pitchero or SOCS?",
      answer: "Go to Profile > Integrations and select the app you want to connect. Enter your login credentials for that service, and Cluubz will automatically sync your fixtures and activities."
    },
    {
      question: "How do I add the home screen widget?",
      answer: "On iPhone: Long-press your home screen, tap the '+' or 'Edit' button in the top-left corner, search for 'Cluubz', and add your preferred widget size."
    },
    {
      question: "Can I share activities with other parents?",
      answer: "Yes! Go to the Connections tab to find and connect with other parents. Once connected, you can share activity information and coordinate schedules together."
    },
    {
      question: "How do I set up the term planner?",
      answer: "Go to a child's profile and tap 'Set Up Term Planner'. Enter your term dates and the default school times. You can then add regular weekly activities and clubs."
    },
    {
      question: "Why isn't my data syncing from a connected app?",
      answer: "Try disconnecting and reconnecting the integration in Profile > Integrations. Make sure your login credentials for that service are still valid."
    },
    {
      question: "How do I delete my account?",
      answer: "Go to Profile and scroll down to the 'Delete Account' button. You'll need to confirm by typing DELETE and entering your password. All your data will be permanently removed immediately. You can also contact us at support@cluubz.com for assistance."
    },
    {
      question: "Is my data secure?",
      answer: "Yes. We use industry-standard encryption to protect your data. We never sell your personal information. See our Privacy Policy for more details."
    }
  ];

  return (
    <div className="legal-page">
      <div className="legal-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          &larr; Back
        </button>
        <img src="/cluubz-logo-white.png" alt="Cluubz" className="legal-logo" />
      </div>

      <div className="legal-content support-content">
        <h1>Support</h1>
        <p className="support-intro">
          Need help with Cluubz? We're here to help! Check our frequently asked questions below
          or contact our support team.
        </p>

        <section className="contact-section">
          <h2>Contact Us</h2>
          <div className="contact-cards">
            <div className="contact-card">
              <div className="contact-icon">&#x2709;</div>
              <h3>Email Support</h3>
              <p>Get help via email</p>
              <a href="mailto:support@cluubz.com" className="contact-link">
                support@cluubz.com
              </a>
            </div>
            <div className="contact-card">
              <div className="contact-icon">&#x1F4AC;</div>
              <h3>Response Time</h3>
              <p>We typically respond within</p>
              <span className="highlight">24 hours</span>
            </div>
          </div>
        </section>

        <section className="faq-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
              >
                <button
                  className="faq-question"
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <span className="faq-toggle">{expandedFaq === index ? '−' : '+'}</span>
                </button>
                {expandedFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="helpful-links">
          <h2>Helpful Links</h2>
          <ul>
            <li><a href="/privacy">Privacy Policy</a></li>
            <li><a href="https://www.cluubz.com">Visit our website</a></li>
          </ul>
        </section>

        <section className="feedback-section">
          <h2>Send Feedback</h2>
          <p>
            We love hearing from our users! If you have suggestions for improving Cluubz
            or want to report an issue, please email us at{' '}
            <a href="mailto:feedback@cluubz.com">feedback@cluubz.com</a>.
          </p>
        </section>
      </div>

      <div className="legal-footer">
        <p>&copy; {new Date().getFullYear()} Cluubz. All rights reserved.</p>
      </div>
    </div>
  );
};

export default Support;
